import axios, { AxiosHeaderValue, AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getValueLocalStorage } from '../hooks/use-local-storage.hook';
import { tokenIsExpired } from '../hooks/auth-verify.hook';
import { env } from '../env';

const AXIOS_TIMEOUT = 30000;
const GET_CACHE_TTL = 5 * 60 * 1000;
const GET_CACHE_PREFIX = 'espace-elu:get-cache:';
const inflightGetRequests = new Map<string, Promise<AxiosResponse>>();
type CachedHeaders = Record<string, AxiosHeaderValue>;

type SerializedGetCacheEntry = {
    data: unknown;
    expiresAt: number;
    headers: CachedHeaders;
    status: number;
    statusText: string;
};

//Création d'axios avec une url de base
export const axiosCore = axios.create({
    baseURL: env.REACT_APP_URLAPI,
    timeout: AXIOS_TIMEOUT,
});

//Intercepteur sur les requests.
//Il permet de rajouter le bearer quand il est présent dans le localStorage
axiosCore.interceptors.request.use(config => {
    config.headers = config.headers ?? {};
    config.timeout = config.timeout ?? AXIOS_TIMEOUT;
    //Intercepteur sur les requests.
    //Il permet de rajouter le bearer quand il est présent dans le localStorage
    const user = getValueLocalStorage('user');
    if (user != null) {
        const expiredToken = user.token ? tokenIsExpired(user.token) : false;

        if (!expiredToken && user.token) {
            config.headers['Authorization'] = 'Bearer ' + user.token;
        }

        //Gestion de l'impersonnalisation
        if (user.emailLoggedInAS) {
            const separator = config.url?.includes('?') ? '&' : '?';
            config.url = config.url + `${separator}_switch_user=${user.emailLoggedInAS}`;
        }
    }
    //Intercepteur sur les requests.
    //Il permet de rajouter le bearer quand il est présent dans le localStorage
    if (config.method !== undefined && ['patch'].includes(config.method)) {
        config.headers['content-type'] = 'application/merge-patch+json';
    }

    if (config.method !== undefined && ['post', 'put', 'patch'].includes(config.method)) {
        config.data = cleanJSON(config.data);
    }

    return config;
});
axiosCore.interceptors.response.use(
    response => response,
    err => {
        if (err.code === 'ERR_CANCELED') {
            const cancelReason = err?.config?.signal?.reason;
            if (typeof cancelReason === 'string' && cancelReason.length > 0) {
                err.message = cancelReason;
            }
        }
        return Promise.reject(err);
    }
);

const originalRequest = axiosCore.request.bind(axiosCore);

axiosCore.request = async function cachedRequest(config: InternalAxiosRequestConfig) {
    const requestConfig = normalizeRequestConfig(config);
    const method = (requestConfig.method ?? 'get').toLowerCase();

    if (mustClearCache(method)) {
        clearGetCache();
    }

    if (!isCacheableGetRequest(requestConfig)) {
        return originalRequest(config);
    }

    const cacheKey = buildCacheKey(requestConfig);
    const cachedResponse = getCachedResponse(cacheKey, requestConfig);
    if (cachedResponse) {
        return cachedResponse;
    }

    const currentRequest = inflightGetRequests.get(cacheKey);
    if (currentRequest) {
        return currentRequest;
    }

    const requestPromise = originalRequest(config)
        .then((response: AxiosResponse) => {
            saveCachedResponse(cacheKey, response);
            return response;
        })
        .finally(() => {
            inflightGetRequests.delete(cacheKey);
        });

    inflightGetRequests.set(cacheKey, requestPromise);
    return requestPromise;
} as typeof axiosCore.request;

/**
 * Add json cleaner to set undefined/null values to empty string.
 * This is to prevent axios(json to string using jsonify) from removing those keys
 * when converting json payload to string.
 */
function cleanJSON<T>(json: T): T {
    if (json === null || json === undefined || typeof json !== 'object') {
        return json;
    }

    const mutableJson = json as Record<string, unknown>;
    for (const key in mutableJson) {
        if (mutableJson[key] === undefined || mutableJson[key] === null) {
            mutableJson[key] = '';
        } else if (typeof mutableJson[key] === 'object') {
            mutableJson[key] = cleanJSON(mutableJson[key]);
        }
    }
    return json;
}

function mustClearCache(method?: string) {
    return method !== undefined && ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase());
}

function isCacheableGetRequest(config: Partial<InternalAxiosRequestConfig>) {
    if ((config.method ?? 'get').toLowerCase() !== 'get') {
        return false;
    }

    if (!isSerializableResponseType(config.responseType)) {
        return false;
    }

    return !config.url?.includes('/generate_token') && !config.url?.includes('/api/subscriber-relation/');
}

function isSerializableResponseType(responseType?: string) {
    return responseType === undefined || responseType === 'json' || responseType === 'text';
}

function buildCacheKey(config: Partial<InternalAxiosRequestConfig>) {
    const url = buildRequestUrl(config);
    const responseType = config.responseType ?? 'json';
    const user = getValueLocalStorage('user');
    const userScope = user?.emailLoggedInAS ?? user?.id ?? 'public';

    return `${(config.method ?? 'get').toLowerCase()}:${responseType}:${userScope}:${url}`;
}

function buildRequestUrl(config: Partial<InternalAxiosRequestConfig>) {
    const baseUrl = config.baseURL ?? axiosCore.defaults.baseURL ?? globalThis.location.origin;
    const requestUrl = new URL(config.url ?? '', baseUrl);

    if (config.params) {
        Object.entries(config.params).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach(arrayValue => {
                    requestUrl.searchParams.append(key, String(arrayValue));
                });
                return;
            }

            requestUrl.searchParams.append(key, String(value));
        });
    }

    requestUrl.searchParams.sort();

    return requestUrl.toString();
}

function getCachedResponse(cacheKey: string, config: Partial<InternalAxiosRequestConfig>): AxiosResponse | undefined {
    const cacheStorage = getCacheStorage();
    if (!cacheStorage) {
        return undefined;
    }

    const cachedValue = cacheStorage.getItem(GET_CACHE_PREFIX + cacheKey);
    if (!cachedValue) {
        return undefined;
    }

    try {
        const parsedValue = JSON.parse(cachedValue) as SerializedGetCacheEntry;

        if (parsedValue.expiresAt <= Date.now()) {
            cacheStorage.removeItem(GET_CACHE_PREFIX + cacheKey);
            return undefined;
        }

        return {
            config: config as InternalAxiosRequestConfig,
            data: parsedValue.data,
            headers: AxiosHeaders.from(normalizeCachedHeaders(parsedValue.headers)),
            request: undefined,
            status: parsedValue.status,
            statusText: parsedValue.statusText,
        };
    } catch (error) {
        cacheStorage.removeItem(GET_CACHE_PREFIX + cacheKey);
        return undefined;
    }
}

function saveCachedResponse(cacheKey: string, response: AxiosResponse) {
    if (!isSerializableResponseType(response.config.responseType)) {
        return;
    }

    const cacheStorage = getCacheStorage();
    if (!cacheStorage) {
        return;
    }

    const serializedResponse: SerializedGetCacheEntry = {
        data: response.data,
        expiresAt: Date.now() + GET_CACHE_TTL,
        headers: normalizeCachedHeaders(response.headers),
        status: response.status,
        statusText: response.statusText,
    };

    try {
        cacheStorage.setItem(GET_CACHE_PREFIX + cacheKey, JSON.stringify(serializedResponse));
    } catch (error) {
        clearGetCache();
    }
}

function clearGetCache() {
    inflightGetRequests.clear();

    const cacheStorage = getCacheStorage();
    if (!cacheStorage) {
        return;
    }

    const keysToDelete: string[] = [];
    for (let index = 0; index < cacheStorage.length; index++) {
        const key = cacheStorage.key(index);
        if (key?.startsWith(GET_CACHE_PREFIX)) {
            keysToDelete.push(key);
        }
    }

    keysToDelete.forEach(key => {
        cacheStorage.removeItem(key);
    });
}

function getCacheStorage() {
    if (typeof globalThis.window === 'undefined' || !globalThis.sessionStorage) {
        return undefined;
    }

    return globalThis.sessionStorage;
}

function normalizeCachedHeaders(headers: unknown): CachedHeaders {
    const normalizedHeaders: CachedHeaders = {};

    Object.entries((headers ?? {}) as Record<string, AxiosHeaderValue | undefined>).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedHeaders[key] = value;
        }
    });

    return normalizedHeaders;
}

function normalizeRequestConfig(configOrUrl: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    return {
        ...configOrUrl,
        method: configOrUrl.method ?? 'get',
    };
}
